import {
  Category,
  getCategoryByID,
  isLeafCategory,
} from '@sbt-web/category-utils';
import { RecentSearchOption } from '@sbt-web/search-autocomplete';
import { HereAddress, TuttaItalia } from '@sbt-web/networking';
import {
  AdTypes,
  CategoryID,
  FilterURI,
  GeoEntry,
  KeyValuePair,
  SearchOrders,
} from '@sbt-web/network/types';
import { dispatchAsyncEvent } from '@tools/eventHelpers';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

const TutteLeCategorie = getCategoryByID(CategoryID.Tutte);

const uuid = (): string | null => {
  return __SERVER__ ? null : crypto.randomUUID();
};

const serversideSearchOrigin = 'ServersideSearch';

/**
 * Any change to the search state on the listing will trigger a reload of the
 * ads on the page.
 */
export type SearchState = {
  id?: string | null;
  query?: string;
  category: Category;
  geo: GeoEntry;
  searchNearRegions?: boolean;
  radiusSearch?: RadiusSearch | null;
  // TODO: remove priority: 2
  qso: boolean;
  // TODO: remove priority: 3
  includeShippableOnly: boolean;
  // TODO: remove priority: 1
  includeUrgent: boolean;
  adType: AdTypes;
  filters: Record<string, string>;
  filtersLabels: Record<string, string>;
  filtersDialogOpen: boolean;
  radiusDialogOpen: boolean;
  isUpdating: boolean;
  page: number;
  orderIndex: number;
  filterOrigin?: string;
};

export type RadiusSearch = {
  center: { lat: number; lng: number };
  radiusMeters: number;
  friendlyName: string;
  addressToTrack?: HereAddress;
};

export const initialState: SearchState = {
  id: null,
  query: '',
  category: TutteLeCategorie,
  adType: TutteLeCategorie.defaultAdType,
  geo: {
    region: TuttaItalia,
  },
  qso: false,
  includeShippableOnly: false,
  includeUrgent: false,
  filters: {},
  filtersLabels: {},
  page: 1,
  orderIndex: 0,
  isUpdating: false,
  filtersDialogOpen: false,
  radiusDialogOpen: false,
  filterOrigin: serversideSearchOrigin,
};

function dispatchFilterChangedEvent(uri: string): void {
  dispatchAsyncEvent(`subito:filterChanged:${uri}`);
}

const updateFilterOriginOnClient = (origin?: string): string | undefined => {
  if (__SERVER__) {
    return serversideSearchOrigin;
  } else {
    return origin;
  }
};

const immobiliCategoryIds: CategoryID[] = [
  CategoryID.Immobili,
  CategoryID.Appartamenti,
  CategoryID.CamerePostiLetto,
  CategoryID.Ville,
  CategoryID.TerreniRustici,
  CategoryID.GarageBox,
  CategoryID.LoftMansarde,
  CategoryID.CasaPersona,
  CategoryID.UfficiLocali,
];

type FilterUpdateValue = KeyValuePair & { uri: string };

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    updateSearchId: (state, action: PayloadAction<string>) => {
      state.id = action.payload;
    },

    updateCategory: (state, action: PayloadAction<Category>) => {
      if (state.category.id === action.payload.id) {
        return;
      }

      dispatchFilterChangedEvent(FilterURI.Category);

      if (
        !immobiliCategoryIds.includes(action.payload.id) &&
        state.radiusSearch != null
      ) {
        state.id = uuid();
        state.page = 1;
        state.category = action.payload;
        state.adType = action.payload.defaultAdType;
        state.geo = {
          region: TuttaItalia,
        };
        state.orderIndex = 0;
        state.filters = {};
        state.filtersLabels = {};
        state.includeShippableOnly = false;
        state.includeUrgent = false;
        state.radiusSearch = null;
        state.filterOrigin = updateFilterOriginOnClient('CategoryComponent');
        return;
      }

      state.id = uuid();
      state.page = 1;
      state.category = action.payload;
      state.adType = action.payload.defaultAdType;

      if (state.geo) {
        if (!(isLeafCategory(action.payload) && action.payload.zoneSupport)) {
          delete state.geo.zone;
        }
      }

      state.orderIndex = 0;
      state.filters = {};
      state.filtersLabels = {};
      state.includeShippableOnly = false;
      state.includeUrgent = false;
      state.filterOrigin = updateFilterOriginOnClient('CategoryComponent');
    },

    updateGeo: {
      reducer(
        state,
        action: PayloadAction<{ geo: GeoEntry; searchNearRegions: boolean }>
      ) {
        const { geo, searchNearRegions } = action.payload;

        if (state.geo === geo || geo == null) {
          return;
        }

        dispatchFilterChangedEvent(FilterURI.Geo);

        state.id = uuid();
        state.page = 1;
        state.geo = geo;
        state.searchNearRegions = searchNearRegions;
        state.radiusSearch = null;
        state.filterOrigin = updateFilterOriginOnClient('GeoSearch');
      },

      prepare(geo: GeoEntry, searchNearRegions = false) {
        return {
          payload: { geo, searchNearRegions },
        };
      },
    },

    updateRadiusSearch: (
      state,
      action: PayloadAction<{
        radiusSearch: RadiusSearch;
        isFinalSearch: boolean;
      }>
    ) => {
      dispatchFilterChangedEvent(FilterURI.Geo);

      state.id = uuid();
      state.page = 1;
      state.radiusSearch = action.payload.radiusSearch;
      state.geo = { region: TuttaItalia };
      state.searchNearRegions = false;
      state.filterOrigin = updateFilterOriginOnClient(
        action.payload.isFinalSearch ? 'RadiusDialog' : undefined
      );
    },

    updateQuery: (state, action: PayloadAction<string | undefined>) => {
      if (state.query === action.payload) {
        return;
      }

      dispatchFilterChangedEvent('/query');

      state.id = uuid();
      state.page = 1;
      state.query = action.payload;
      state.filterOrigin = updateFilterOriginOnClient('MainSearch');
    },

    updateAdType: (state, action: PayloadAction<AdTypes>) => {
      if (state.adType === action.payload) {
        return;
      }

      dispatchFilterChangedEvent(FilterURI.AdType);

      state.id = uuid();
      state.adType = action.payload;
      state.page = 1;
      state.filters = {};
      state.filtersLabels = {};
      state.includeShippableOnly = false;
      state.includeUrgent = false;
      state.filterOrigin = updateFilterOriginOnClient(
        state.filtersDialogOpen ? undefined : 'FilterUI'
      );
    },

    updateQSO: (state, action: PayloadAction<boolean>) => {
      dispatchFilterChangedEvent(FilterURI.QuerySubjectOnly);

      state.id = uuid();
      state.page = 1;
      state.qso = action.payload;
      state.filterOrigin = updateFilterOriginOnClient(
        state.filtersDialogOpen ? undefined : 'FilterUI'
      );
    },

    updateShippableOnly: (state, action: PayloadAction<boolean>) => {
      dispatchFilterChangedEvent(FilterURI.ItemShippable);

      state.id = uuid();
      state.page = 1;
      state.includeShippableOnly = action.payload;
      state.filterOrigin = updateFilterOriginOnClient(
        state.filtersDialogOpen ? undefined : 'FilterUI'
      );
    },

    updateUrgent: (state, action: PayloadAction<boolean>) => {
      dispatchFilterChangedEvent(FilterURI.ItemUrgent);

      state.id = uuid();
      state.page = 1;
      state.includeUrgent = action.payload;
      state.filterOrigin = updateFilterOriginOnClient(
        state.filtersDialogOpen ? undefined : 'FilterUI'
      );
    },

    updateFilter: (state, action: PayloadAction<FilterUpdateValue>) => {
      if (action.payload.value === state.filters[action.payload.key]) {
        return;
      }

      state.id = uuid();
      state.page = 1;

      if (action.payload.value) {
        state.filters[action.payload.key] = action.payload.value;
        delete state.filtersLabels[action.payload.key];
      } else {
        delete state.filters[action.payload.key];
      }

      state.filterOrigin = updateFilterOriginOnClient(
        state.filtersDialogOpen ? undefined : 'FilterUI'
      );

      const uri = action.payload.uri;

      if (uri) {
        dispatchFilterChangedEvent(uri);
      }
    },

    declareFinalSearch: (state) => {
      state.id = uuid();
      state.filterOrigin = updateFilterOriginOnClient('FilterUI');
    },

    updateFilterLabel: (state, action: PayloadAction<KeyValuePair>) => {
      state.id = uuid();
      state.page = 1;

      if (action.payload.value) {
        state.filtersLabels[action.payload.key] = action.payload.value;
      } else {
        delete state.filtersLabels[action.payload.key];
      }
    },

    updateFiltersLabelsBulk: (
      state,
      action: PayloadAction<Record<string, string>>
    ) => {
      state.id = uuid();
      state.filtersLabels = action.payload;
    },

    resetFilters: (state) => {
      dispatchFilterChangedEvent('reset');

      state.id = uuid();
      state.qso = false;
      state.adType = state.category.defaultAdType;
      state.page = 1;
      state.filters = {};
      state.filtersLabels = {};
      state.filterOrigin = updateFilterOriginOnClient('FilterReset');
    },

    setFiltersDialogStatus: (state, action: PayloadAction<boolean>) => {
      dispatchAsyncEvent(`subito:listingCovered:${action.payload}`);
      state.filtersDialogOpen = action.payload;
    },

    setRadiusDialogStatus: (state, action: PayloadAction<boolean>) => {
      dispatchAsyncEvent(`subito:listingCovered:${action.payload}`);
      state.radiusDialogOpen = action.payload;
    },

    startUpdating: (state) => {
      state.isUpdating = true;
    },

    stopUpdating: (state) => {
      state.isUpdating = false;
    },

    updateOrder: (state, action: PayloadAction<SearchOrders>) => {
      const index = state.category.orders.findIndex(
        (o) => o === action.payload
      );

      if (index === state.orderIndex) {
        return;
      }

      dispatchFilterChangedEvent(FilterURI.Order);

      state.id = uuid();
      state.orderIndex = index >= 0 ? index : 0;
      state.page = 1;
      state.filterOrigin = updateFilterOriginOnClient('OrderComponent');
    },

    updatePage: (state, action: PayloadAction<number>) => {
      dispatchFilterChangedEvent(FilterURI.Page);

      state.page = action.payload;
      state.filterOrigin = updateFilterOriginOnClient('PaginationComponent');
    },

    updateRecentSearch: (state, action: PayloadAction<RecentSearchOption>) => {
      const {
        qso,
        shp: includeShippableOnly,
        urg: includeUrgent,
        filters,
      } = action.payload?.allFilters ?? {
        qso: false,
        shp: false,
        urg: false,
        filters: undefined,
      };

      state.id = uuid();
      state.adType = action.payload.adType as AdTypes;
      state.query = action.payload.query;
      state.category = action.payload.category as Category;
      state.geo = action.payload.geo.geoValues;
      state.radiusSearch = action.payload.geo.radiusValues;
      state.qso = qso;
      state.includeShippableOnly = includeShippableOnly;
      state.includeUrgent = includeUrgent;
      state.filters = filters ?? {};
      state.searchNearRegions = action.payload.searchNearRegions;
    },

    updateSearch: (_state, action: PayloadAction<SearchState>) => {
      return {
        ...action.payload,
        id: uuid(),
      };
    },
  },
});

export const search = searchSlice.reducer;

export const {
  updateSearchId,
  updateCategory,
  updateGeo,
  updateRadiusSearch,
  updateQuery,
  updateAdType,
  updateQSO,
  updateShippableOnly,
  updateUrgent,
  updateFilter,
  declareFinalSearch,
  updateFilterLabel,
  updateFiltersLabelsBulk,
  resetFilters,
  setFiltersDialogStatus,
  setRadiusDialogStatus,
  startUpdating,
  stopUpdating,
  updateOrder,
  updatePage,
  updateRecentSearch,
  updateSearch,
} = searchSlice.actions;
